import { useState, useEffect, useCallback } from 'react';
import {
  Plus, Percent, DollarSign, Gift, Package, ShoppingBag, Zap,
  Search, ChevronLeft, ChevronRight, Loader2, Tag, Calendar,
  ToggleLeft, ToggleRight, Clock, AlertTriangle, X
} from 'lucide-react';
import { useAuthStore } from '@/stores/auth-store';
import { promotionsService, Promotion, PromotionRule, CreatePromotionDto } from '@/services/promotions.service';
import { productosService } from '@/services/productos.service';
import { useApiError } from '@/hooks/useApiError';
import { ApiErrorDisplay } from '@/components/ApiErrorDisplay';
import { dialogs } from '@/stores/dialog-store';

const typeConfig: Record<string, { label: string; icon: any; color: string; description: string }> = {
  PERCENTAGE: { label: '% Descuento', icon: Percent, color: 'bg-blue-50 text-blue-600', description: 'Porcentaje de descuento en productos seleccionados' },
  FIXED: { label: 'Descuento Fijo', icon: DollarSign, color: 'bg-emerald-50 text-emerald-600', description: 'Monto fijo de descuento' },
  BUY_X_GET_Y: { label: '2x1 / Lleva X paga Y', icon: Gift, color: 'bg-purple-50 text-purple-600', description: 'Compra cierta cantidad y recibe gratis' },
  COMBO: { label: 'Combo / Paquete', icon: Package, color: 'bg-orange-50 text-orange-600', description: 'Paquete de productos a precio especial' },
  VOLUME: { label: 'Volumen', icon: ShoppingBag, color: 'bg-pink-50 text-pink-600', description: 'Descuento por cantidad mínima' },
};

const emptyRule: PromotionRule = {
  ruleType: 'ANY',
  requiredQuantity: 2,
  freeQuantity: 1,
  discountPercent: 10,
  discountAmount: 0,
  comboPrice: 0,
  maxComboUnits: 10,
};

export function PromotionsPage() {
  const { user } = useAuthStore();
  const { error, errorType, handleApiError, handleAuthRedirect, clearError } = useApiError();

  const [promotions, setPromotions] = useState<Promotion[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterType, setFilterType] = useState('');

  // Form state
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState<CreatePromotionDto>({
    companyId: '',
    name: '',
    description: '',
    type: 'PERCENTAGE',
    discountPercent: 10,
    discountAmount: 0,
    priority: 0,
    maxUses: undefined,
    minPurchaseAmount: undefined,
    startDate: '',
    endDate: '',
    applyToAllBranches: true,
    rules: [{ ...emptyRule }],
  });

  // Product search for rules
  const [productSearch, setProductSearch] = useState('');
  const [productResults, setProductResults] = useState<any[]>([]);
  const [showProductPicker, setShowProductPicker] = useState<string | null>(null);

  const loadPromotions = useCallback(async () => {
    if (!user?.company?.id) return;
    setLoading(true);
    try {
      const result = await promotionsService.findAll({
        companyId: user.company.id,
        type: filterType || undefined,
        page,
        limit: 20,
      });
      setPromotions(result.data || []);
      setTotal(result.total || 0);
      setTotalPages(result.totalPages || 1);
    } catch (err: any) { handleApiError(err); }
    setLoading(false);
  }, [user, filterType, page]);

  useEffect(() => { loadPromotions(); }, [loadPromotions]);

  const filtered = search
    ? promotions.filter(p =>
        p.name.toLowerCase().includes(search.toLowerCase()) ||
        p.description?.toLowerCase().includes(search.toLowerCase())
      )
    : promotions;

  const openCreateForm = () => {
    setEditingId(null);
    setForm({
      companyId: user?.company?.id || '',
      name: '',
      description: '',
      type: 'PERCENTAGE',
      discountPercent: 10,
      discountAmount: 0,
      priority: 0,
      maxUses: undefined,
      minPurchaseAmount: undefined,
      startDate: '',
      endDate: '',
      applyToAllBranches: true,
      rules: [{ ...emptyRule }],
    });
    setShowForm(true);
  };

  const openEditForm = async (promotion: Promotion) => {
    setEditingId(promotion.id);
    setForm({
      companyId: promotion.companyId,
      name: promotion.name,
      description: promotion.description || '',
      type: promotion.type,
      discountPercent: promotion.discountPercent ? Number(promotion.discountPercent) : undefined,
      discountAmount: promotion.discountAmount ? Number(promotion.discountAmount) : undefined,
      priority: promotion.priority,
      maxUses: promotion.maxUses || undefined,
      minPurchaseAmount: promotion.minPurchaseAmount ? Number(promotion.minPurchaseAmount) : undefined,
      startDate: promotion.startDate ? promotion.startDate.slice(0, 10) : '',
      endDate: promotion.endDate ? promotion.endDate.slice(0, 10) : '',
      applyToAllBranches: promotion.applyToAllBranches,
      rules: promotion.rules.length > 0
        ? promotion.rules.map(r => ({
            ruleType: r.ruleType,
            productId: r.productId,
            productName: (r as any).productName,
            categoryId: r.categoryId,
            brandId: r.brandId,
            requiredQuantity: r.requiredQuantity || 2,
            freeQuantity: r.freeQuantity || 1,
            freeProductId: r.freeProductId,
            comboPrice: r.comboPrice ? Number(r.comboPrice) : 0,
            maxComboUnits: r.maxComboUnits || 10,
            discountPercent: r.discountPercent ? Number(r.discountPercent) : undefined,
            discountAmount: r.discountAmount ? Number(r.discountAmount) : undefined,
          }))
        : [{ ...emptyRule }],
    });
    setShowForm(true);
  };

  const handleDeactivate = async (id: string, name: string) => {
    if (!await dialogs.confirm(`¿Desactivar "${name}"?`, { title: 'Desactivar Promoción' })) return;
    try {
      await promotionsService.deactivate(id);
      loadPromotions();
    } catch (err: any) { handleApiError(err); }
  };

  const addRule = () => {
    setForm(f => ({ ...f, rules: [...(f.rules || []), { ...emptyRule }] }));
  };

  const removeRule = (index: number) => {
    setForm(f => ({ ...f, rules: (f.rules || []).filter((_, i) => i !== index) }));
  };

  const updateRule = (index: number, updates: Partial<PromotionRule>) => {
    setForm(f => ({
      ...f,
      rules: (f.rules || []).map((r, i) => i === index ? { ...r, ...updates } : r),
    }));
  };

  const searchProducts = async (query: string) => {
    if (!user?.company?.id || query.length < 1) return;
    try {
      const result = await productosService.findAll({ companyId: user.company.id, search: query, limit: 10 });
      setProductResults(result.data || []);
    } catch { /* empty */ }
  };

  const selectProductForRule = (index: number, product: any) => {
    updateRule(index, { productId: product.id, productName: product.name, ruleType: 'PRODUCT' });
    setShowProductPicker(null);
    setProductSearch('');
  };

  const handleSubmit = async () => {
    if (!user?.company?.id || !form.name.trim()) return;
    setSaving(true);
    try {
      const payload = {
        ...form,
        companyId: user.company.id,
        rules: form.type === 'COMBO'
          ? (form.rules || [])
          : (form.rules || []).filter(r => {
              if (form.type === 'BUY_X_GET_Y') return r.requiredQuantity && r.requiredQuantity > 0;
              if (r.ruleType === 'PRODUCT') return r.productId;
              if (r.ruleType === 'ANY') return true;
              return true;
            }),
      };

      if (editingId) {
        await promotionsService.update(editingId, payload);
        await dialogs.alert('Promoción actualizada exitosamente');
      } else {
        await promotionsService.create(payload as CreatePromotionDto);
        await dialogs.alert('Promoción creada exitosamente');
      }
      setShowForm(false);
      loadPromotions();
    } catch (err: any) {
      const msg = err?.response?.data?.message || 'Error al guardar promoción';
      await dialogs.alert(msg);
    } finally {
      setSaving(false);
    }
  };

  const formatCurrency = (n: number) => '$' + n.toLocaleString('es-MX', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  const formatDate = (d: string) => new Date(d).toLocaleDateString('es-MX', { day: '2-digit', month: 'short', year: 'numeric' });
  const isExpired = (endDate?: string) => endDate ? new Date(endDate) < new Date() : false;
  const isPending = (startDate?: string) => startDate ? new Date(startDate) > new Date() : false;

  const TypeIcon = (type: string) => typeConfig[type]?.icon || Tag;

  return (
    <div className="w-full">
      <ApiErrorDisplay error={error} errorType={errorType} onRetry={loadPromotions} onLogin={handleAuthRedirect} />
      {!error && (
        <>
          <div className="flex items-center justify-between mb-6">
            <div>
              <h1 className="text-2xl font-bold">Promociones</h1>
              <p className="text-sm text-ios-gray mt-1">
                {total} promociones {filterType && `· Tipo: ${typeConfig[filterType]?.label}`}
              </p>
            </div>
            <button onClick={openCreateForm} className="ios-button flex items-center gap-2">
              <Plus size={16} /> Nueva Promoción
            </button>
          </div>

          {/* Filters */}
          <div className="flex items-center gap-3 mb-4">
            <div className="relative flex-1 max-w-sm">
              <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-ios-gray" />
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Buscar promociones..."
                className="ios-input w-full pl-9 h-9 text-sm"
              />
            </div>
            <select
              value={filterType}
              onChange={(e) => { setFilterType(e.target.value); setPage(1); }}
              className="ios-input h-9 text-sm"
            >
              <option value="">Todos los tipos</option>
              {Object.entries(typeConfig).map(([key, cfg]) => (
                <option key={key} value={key}>{cfg.label}</option>
              ))}
            </select>
          </div>

          {/* Promotions Grid */}
          {loading ? (
            <div className="flex justify-center py-16">
              <Loader2 size={28} className="animate-spin text-ios-gray" />
            </div>
          ) : filtered.length === 0 ? (
            <div className="text-center py-16 text-ios-gray">
              <Tag size={48} className="mx-auto mb-3 opacity-50" />
              <p className="font-medium">No hay promociones</p>
              <p className="text-sm mt-1">Crea la primera promoción para empezar</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
              {filtered.map((promo) => {
                const Icon = TypeIcon(promo.type);
                const cfg = typeConfig[promo.type];
                const expired = isExpired(promo.endDate);
                const pending = isPending(promo.startDate);

                return (
                  <div key={promo.id} className={`bg-white rounded-xl border overflow-hidden transition-all hover:shadow-md ${
                    promo.status === 'INACTIVE' ? 'border-gray-200 opacity-60' :
                    expired ? 'border-red-200' :
                    pending ? 'border-yellow-200' :
                    'border-gray-100'
                  }`}>
                    <div className="p-4">
                      {/* Header */}
                      <div className="flex items-start justify-between mb-3">
                        <div className="flex items-center gap-2">
                          <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${cfg?.color || 'bg-gray-100 text-gray-600'}`}>
                            <Icon size={16} />
                          </div>
                          <div>
                            <h3 className="font-semibold text-gray-900">{promo.name}</h3>
                            <span className={`text-[10px] font-medium px-1.5 py-0.5 rounded-full ${cfg?.color || ''}`}>
                              {cfg?.label || promo.type}
                            </span>
                          </div>
                        </div>
                        <div className="flex items-center gap-1">
                          {promo.status === 'ACTIVE' ? (
                            <ToggleRight size={20} className="text-green-500" />
                          ) : (
                            <ToggleLeft size={20} className="text-gray-300" />
                          )}
                        </div>
                      </div>

                      {/* Description */}
                      {promo.description && (
                        <p className="text-xs text-ios-gray mb-3 line-clamp-2">{promo.description}</p>
                      )}

                      {/* Discount Value */}
                      <div className="bg-gray-50 rounded-lg p-3 mb-3">
                        {promo.type === 'PERCENTAGE' && (
                          <div className="text-center">
                            <span className="text-2xl font-black text-blue-600">{Number(promo.discountPercent).toFixed(0)}%</span>
                            <p className="text-[10px] text-ios-gray">DESCUENTO</p>
                          </div>
                        )}
                        {promo.type === 'FIXED' && (
                          <div className="text-center">
                            <span className="text-2xl font-black text-emerald-600">{formatCurrency(Number(promo.discountAmount || 0))}</span>
                            <p className="text-[10px] text-ios-gray">DESCUENTO</p>
                          </div>
                        )}
                        {promo.type === 'BUY_X_GET_Y' && (
                          <div className="text-center">
                            <span className="text-xl font-black text-purple-600">
                              {promo.rules[0]?.requiredQuantity || 2}×{promo.rules[0]?.freeQuantity || 1}
                            </span>
                            <p className="text-[10px] text-ios-gray">LLEVA {promo.rules[0]?.requiredQuantity || 2} PAGA {(promo.rules[0]?.requiredQuantity || 2) - (promo.rules[0]?.freeQuantity || 1)}</p>
                          </div>
                        )}
                        {promo.type === 'COMBO' && (
                          <div className="text-center">
                            <span className="text-lg font-black text-orange-600">{promo.rules.length} productos</span>
                            <p className="text-[10px] text-ios-gray">PRECIO ESPECIAL</p>
                          </div>
                        )}
                        {promo.type === 'VOLUME' && (
                          <div className="text-center">
                            <span className="text-2xl font-black text-pink-600">{Number(promo.discountPercent).toFixed(0)}%</span>
                            <p className="text-[10px] text-ios-gray">DESDE {promo.rules[0]?.requiredQuantity || 5} PZAS</p>
                          </div>
                        )}
                      </div>

                      {/* Rules summary */}
                      {promo.rules.length > 0 && (
                        <div className="space-y-1 mb-3">
                          {promo.rules.slice(0, 2).map((rule, idx) => (
                            <div key={idx} className="text-[10px] text-ios-gray flex items-center gap-1">
                              <div className="w-1 h-1 rounded-full bg-ios-gray" />
                              {rule.ruleType === 'ANY' && <span>Cualquier producto</span>}
                              {rule.ruleType === 'PRODUCT' && <span>Producto específico</span>}
                              {rule.ruleType === 'CATEGORY' && <span>Categoría específica</span>}
                              {rule.ruleType === 'BRAND' && <span>Marca específica</span>}
                              {rule.ruleType === 'COMBO_ITEM' && <span>Item de combo</span>}
                              {rule.requiredQuantity && <span>· Mín: {rule.requiredQuantity}</span>}
                            </div>
                          ))}
                          {promo.rules.length > 2 && (
                            <p className="text-[10px] text-ios-gray">+{promo.rules.length - 2} reglas más</p>
                          )}
                        </div>
                      )}

                      {/* Meta info */}
                      <div className="flex items-center justify-between text-[10px] text-ios-gray border-t border-gray-100 pt-2">
                        <div className="flex items-center gap-2">
                          {promo.startDate && (
                            <span className="flex items-center gap-1">
                              <Calendar size={10} />
                              {formatDate(promo.startDate)}
                            </span>
                          )}
                          {promo.endDate && (
                            <span className={`flex items-center gap-1 ${expired ? 'text-red-500' : ''}`}>
                              → {formatDate(promo.endDate)}
                            </span>
                          )}
                        </div>
                        <span>Usos: {promo.usedCount}{promo.maxUses ? `/${promo.maxUses}` : ''}</span>
                      </div>

                      {/* Status badges */}
                      <div className="flex flex-wrap gap-1 mt-2">
                        {expired && (
                          <span className="text-[9px] font-medium bg-red-50 text-red-600 px-1.5 py-0.5 rounded-full">VENCIDA</span>
                        )}
                        {pending && (
                          <span className="text-[9px] font-medium bg-yellow-50 text-yellow-600 px-1.5 py-0.5 rounded-full">PRÓXIMA</span>
                        )}
                        {promo.status === 'INACTIVE' && (
                          <span className="text-[9px] font-medium bg-gray-100 text-gray-500 px-1.5 py-0.5 rounded-full">INACTIVA</span>
                        )}
                        {promo.minPurchaseAmount && Number(promo.minPurchaseAmount) > 0 && (
                          <span className="text-[9px] font-medium bg-blue-50 text-blue-600 px-1.5 py-0.5 rounded-full">
                            Mín: {formatCurrency(Number(promo.minPurchaseAmount))}
                          </span>
                        )}
                      </div>
                    </div>

                    {/* Actions */}
                    <div className="border-t border-gray-100 flex">
                      <button
                        onClick={() => openEditForm(promo)}
                        className="flex-1 text-xs font-medium text-ios-blue py-2.5 hover:bg-blue-50 transition-colors"
                      >
                        Editar
                      </button>
                      <button
                        onClick={() => handleDeactivate(promo.id, promo.name)}
                        className="flex-1 text-xs font-medium text-red-500 py-2.5 hover:bg-red-50 transition-colors border-l border-gray-100"
                      >
                        {promo.status === 'ACTIVE' ? 'Desactivar' : 'Eliminar'}
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between px-4 py-3 mt-4">
              <span className="text-xs text-ios-gray">Página {page} de {totalPages}</span>
              <div className="flex gap-1">
                <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1} className="p-1.5 rounded-lg hover:bg-ios-gray-6 disabled:opacity-50">
                  <ChevronLeft size={16} />
                </button>
                <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages} className="p-1.5 rounded-lg hover:bg-ios-gray-6 disabled:opacity-50">
                  <ChevronRight size={16} />
                </button>
              </div>
            </div>
          )}

          {/* Create/Edit Modal */}
          {showForm && (
            <div className="fixed inset-0 bg-black/30 backdrop-blur-sm z-50 flex items-center justify-center p-4">
              <div className="bg-white rounded-[20px] w-full max-w-2xl max-h-[90vh] flex flex-col shadow-ios-xl">
                <div className="flex items-center justify-between p-4 border-b border-ios-gray-5">
                  <h3 className="font-semibold text-lg">
                    {editingId ? 'Editar Promoción' : 'Nueva Promoción'}
                  </h3>
                  <button onClick={() => setShowForm(false)} className="p-2 rounded-lg hover:bg-ios-gray-6 transition-colors">
                    <X size={18} />
                  </button>
                </div>

                <div className="flex-1 overflow-y-auto p-4 space-y-4">
                  {/* Basic Info */}
                  <div className="grid grid-cols-2 gap-4">
                    <div className="col-span-2">
                      <label className="text-xs text-ios-gray mb-1 block">Nombre *</label>
                      <input
                        type="text"
                        value={form.name}
                        onChange={(e) => setForm(f => ({ ...f, name: e.target.value }))}
                        className="ios-input w-full"
                        placeholder="Ej: 2x1 en Sabritas"
                      />
                    </div>
                    <div className="col-span-2">
                      <label className="text-xs text-ios-gray mb-1 block">Descripción</label>
                      <textarea
                        value={form.description}
                        onChange={(e) => setForm(f => ({ ...f, description: e.target.value }))}
                        className="ios-input w-full min-h-[60px] resize-none"
                        placeholder="Describe cómo funciona la promoción..."
                      />
                    </div>
                    <div className="col-span-2">
                      <label className="text-xs text-ios-gray mb-1 block">Tipo de Promoción</label>
                      <select
                        value={form.type}
                        onChange={(e) => setForm(f => ({ ...f, type: e.target.value }))}
                        className="ios-input w-full"
                      >
                        <option value="PERCENTAGE">% Descuento</option>
                        <option value="FIXED">Descuento Fijo ($)</option>
                        <option value="BUY_X_GET_Y">2x1 / Lleva X paga Y</option>
                        <option value="COMBO">Combo / Paquete</option>
                        <option value="VOLUME">Descuento por Volumen</option>
                      </select>
                    </div>
                  </div>

                  {/* Type-specific fields */}
                  {(form.type === 'PERCENTAGE' || form.type === 'VOLUME') && (
                    <div>
                      <label className="text-xs text-ios-gray mb-1 block">Porcentaje de Descuento</label>
                      <div className="flex items-center gap-2">
                        <input
                          type="number"
                          value={form.discountPercent || ''}
                          onChange={(e) => setForm(f => ({ ...f, discountPercent: parseFloat(e.target.value) || 0 }))}
                          className="ios-input w-32"
                          min="0" max="100" step="0.5"
                        />
                        <span className="text-lg font-bold text-ios-gray">%</span>
                      </div>
                    </div>
                  )}

                  {form.type === 'FIXED' && (
                    <div>
                      <label className="text-xs text-ios-gray mb-1 block">Monto de Descuento</label>
                      <div className="flex items-center gap-2">
                        <span className="text-lg font-bold text-ios-gray">$</span>
                        <input
                          type="number"
                          value={form.discountAmount || ''}
                          onChange={(e) => setForm(f => ({ ...f, discountAmount: parseFloat(e.target.value) || 0 }))}
                          className="ios-input w-40"
                          min="0" step="0.5"
                        />
                      </div>
                    </div>
                  )}

                  {/* Rules Section */}
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <label className="text-xs font-medium text-ios-gray">Reglas de Aplicación</label>
                      <button onClick={addRule} className="text-xs text-ios-blue hover:underline flex items-center gap-1">
                        <Plus size={12} /> Agregar regla
                      </button>
                    </div>

                    <div className="space-y-3">
                      {(form.rules || []).map((rule, idx) => (
                        <div key={idx} className="bg-ios-gray-6 rounded-[12px] p-3 space-y-2">
                          <div className="flex items-center justify-between">
                            <span className="text-xs font-medium text-ios-gray">Regla #{idx + 1}</span>
                            {(form.rules || []).length > 1 && (
                              <button onClick={() => removeRule(idx)} className="text-red-400 hover:text-red-600 p-1">
                                <X size={14} />
                              </button>
                            )}
                          </div>

                          <div className="grid grid-cols-2 gap-2">
                            <div>
                              <label className="text-[10px] text-ios-gray mb-0.5 block">Aplica a</label>
                              <select
                                value={rule.ruleType}
                                onChange={(e) => updateRule(idx, { ruleType: e.target.value as any })}
                                className="ios-input w-full text-xs"
                              >
                                <option value="ANY">Cualquier producto</option>
                                <option value="PRODUCT">Producto específico</option>
                                <option value="CATEGORY">Categoría</option>
                                <option value="BRAND">Marca</option>
                                {form.type === 'COMBO' && <option value="COMBO_ITEM">Item de combo</option>}
                              </select>
                            </div>

                            {rule.ruleType === 'PRODUCT' && (
                              <div>
                                <label className="text-[10px] text-ios-gray mb-0.5 block">Producto</label>
                                <button
                                  onClick={() => setShowProductPicker(idx.toString())}
                                  className="w-full text-left px-3 py-2 bg-white border border-ios-gray-5 rounded-[10px] text-xs hover:border-ios-blue transition-colors truncate"
                                >
                                  {rule.productName || 'Seleccionar producto...'}
                                </button>
                              </div>
                            )}

                            {(form.type === 'VOLUME' || form.type === 'BUY_X_GET_Y') && (
                              <div>
                                <label className="text-[10px] text-ios-gray mb-0.5 block">
                                  {form.type === 'BUY_X_GET_Y' ? 'Comprar mín.' : 'Cant. mínima'}
                                </label>
                                <input
                                  type="number"
                                  value={rule.requiredQuantity || ''}
                                  onChange={(e) => updateRule(idx, { requiredQuantity: parseInt(e.target.value) || 0 })}
                                  className="ios-input w-full text-xs"
                                  min="1"
                                />
                              </div>
                            )}

                            {form.type === 'BUY_X_GET_Y' && (
                              <div>
                                <label className="text-[10px] text-ios-gray mb-0.5 block">Lleva gratis</label>
                                <input
                                  type="number"
                                  value={rule.freeQuantity || ''}
                                  onChange={(e) => updateRule(idx, { freeQuantity: parseInt(e.target.value) || 1 })}
                                  className="ios-input w-full text-xs"
                                  min="1"
                                />
                              </div>
                            )}

                            {form.type === 'COMBO' && (
                              <>
                                <div>
                                  <label className="text-[10px] text-ios-gray mb-0.5 block">Precio combo</label>
                                  <input
                                    type="number"
                                    value={rule.comboPrice || ''}
                                    onChange={(e) => updateRule(idx, { comboPrice: parseFloat(e.target.value) || 0 })}
                                    className="ios-input w-full text-xs"
                                    min="0" step="0.5"
                                  />
                                </div>
                                <div>
                                  <label className="text-[10px] text-ios-gray mb-0.5 block">Máx. combos</label>
                                  <input
                                    type="number"
                                    value={rule.maxComboUnits || ''}
                                    onChange={(e) => updateRule(idx, { maxComboUnits: parseInt(e.target.value) || 1 })}
                                    className="ios-input w-full text-xs"
                                    min="1"
                                  />
                                </div>
                              </>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Date Range */}
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="text-xs text-ios-gray mb-1 block">Fecha de inicio</label>
                      <input
                        type="date"
                        value={form.startDate}
                        onChange={(e) => setForm(f => ({ ...f, startDate: e.target.value }))}
                        className="ios-input w-full"
                      />
                    </div>
                    <div>
                      <label className="text-xs text-ios-gray mb-1 block">Fecha de fin</label>
                      <input
                        type="date"
                        value={form.endDate}
                        onChange={(e) => setForm(f => ({ ...f, endDate: e.target.value }))}
                        className="ios-input w-full"
                      />
                    </div>
                  </div>

                  {/* Limits */}
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="text-xs text-ios-gray mb-1 block">Usos máximos</label>
                      <input
                        type="number"
                        value={form.maxUses || ''}
                        onChange={(e) => setForm(f => ({ ...f, maxUses: parseInt(e.target.value) || undefined }))}
                        className="ios-input w-full"
                        placeholder="Ilimitado"
                        min="1"
                      />
                    </div>
                    <div>
                      <label className="text-xs text-ios-gray mb-1 block">Compra mínima ($)</label>
                      <input
                        type="number"
                        value={form.minPurchaseAmount || ''}
                        onChange={(e) => setForm(f => ({ ...f, minPurchaseAmount: parseFloat(e.target.value) || undefined }))}
                        className="ios-input w-full"
                        placeholder="Sin mínimo"
                        min="0" step="0.5"
                      />
                    </div>
                    <div>
                      <label className="text-xs text-ios-gray mb-1 block">Prioridad</label>
                      <input
                        type="number"
                        value={form.priority}
                        onChange={(e) => setForm(f => ({ ...f, priority: parseInt(e.target.value) || 0 }))}
                        className="ios-input w-full"
                        min="0"
                      />
                      <p className="text-[10px] text-ios-gray mt-0.5">Menor número = se aplica primero</p>
                    </div>
                    <div>
                      <label className="text-xs text-ios-gray mb-1 block">Sucursales</label>
                      <select
                        value={form.applyToAllBranches ? 'all' : 'specific'}
                        onChange={(e) => setForm(f => ({ ...f, applyToAllBranches: e.target.value === 'all' }))}
                        className="ios-input w-full"
                      >
                        <option value="all">Todas las sucursales</option>
                        <option value="specific">Sucursales específicas</option>
                      </select>
                    </div>
                  </div>
                </div>

                {/* Submit */}
                <div className="p-4 border-t border-ios-gray-5 flex justify-end gap-2">
                  <button onClick={() => setShowForm(false)} className="px-4 py-2.5 rounded-[10px] text-sm border border-ios-gray-5 hover:bg-ios-gray-6">
                    Cancelar
                  </button>
                  <button
                    onClick={handleSubmit}
                    disabled={saving || !form.name.trim()}
                    className="px-6 py-2.5 rounded-[10px] text-sm bg-ios-blue text-white font-medium hover:bg-blue-600 disabled:opacity-50 flex items-center gap-2"
                  >
                    {saving ? <Loader2 size={16} className="animate-spin" /> : null}
                    {editingId ? 'Guardar Cambios' : 'Crear Promoción'}
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Product Picker */}
          {showProductPicker !== null && (
            <div className="fixed inset-0 bg-black/30 backdrop-blur-sm z-[60] flex items-center justify-center p-4">
              <div className="bg-white rounded-[20px] w-full max-w-md shadow-ios-xl">
                <div className="p-4 border-b border-ios-gray-5">
                  <h3 className="font-semibold">Seleccionar Producto</h3>
                </div>
                <div className="p-4">
                  <input
                    type="text"
                    value={productSearch}
                    onChange={(e) => { setProductSearch(e.target.value); if (e.target.value.length > 1) searchProducts(e.target.value); }}
                    placeholder="Buscar producto..."
                    className="ios-input w-full text-sm"
                    autoFocus
                  />
                </div>
                <div className="max-h-60 overflow-y-auto px-4 pb-4 space-y-1">
                  {productResults.length === 0 ? (
                    <p className="text-sm text-ios-gray text-center py-4">{productSearch.length > 1 ? 'Sin resultados' : 'Escribe para buscar...'}</p>
                  ) : (
                    productResults.map((p: any) => (
                      <button
                        key={p.id}
                        onClick={() => selectProductForRule(parseInt(showProductPicker), p)}
                        className="w-full text-left px-3 py-2.5 rounded-[10px] text-sm hover:bg-blue-50 transition-colors flex items-center justify-between"
                      >
                        <div className="flex-1 min-w-0">
                          <p className="font-medium truncate">{p.name}</p>
                          <p className="text-xs text-ios-gray">{p.sku}</p>
                        </div>
                        <span className="text-xs font-semibold text-ios-blue">${Number(p.pricePublic).toFixed(2)}</span>
                      </button>
                    ))
                  )}
                </div>
                <div className="p-4 border-t border-ios-gray-5">
                  <button onClick={() => { setShowProductPicker(null); setProductSearch(''); }} className="w-full px-4 py-2 rounded-[10px] text-sm border border-ios-gray-5 hover:bg-ios-gray-6">
                    Cancelar
                  </button>
                </div>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
